import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type TourType = 'JOIN_TOUR' | 'GROUP_TOUR';
export type TourStatus = 'DRAFT' | 'ACTIVE' | 'CANCELLED' | 'COMPLETED' | 'DELETED';

export interface SaveTourRequest {
  tourCode: string;
  tourName: string;
  country: string;
  tourType: TourType;
  travelDate: string;
  returnDate: string | null;
  tourPrice: number;
  requiredPax: number;
  saleCommission: number;
  administratorCommissionPercent: number;
  programDetail: string | null;
  profilePath: string | null;
  tourPdfDetailPath: string | null;
  note: string | null;
  expenses: Array<{ expenseTypeId: string; amount: number }>;
}

export interface TourAdministratorItem {
  id: string;
  userId: string;
  isPrimary: boolean;
  responsibility: string | null;
  assignedAt: string;
  user: { firstName: string; lastName: string };
}

export interface TourAdministratorOption {
  id: string;
  employeeCode: string;
  firstName: string;
  lastName: string;
  department: { name: string };
}

export interface TourCodeOption {
  id: string;
  tourCode: string;
  tourName: string;
}

export interface CreateTourAdministratorRequest {
  userId: string;
  responsibility: string | null;
  isPrimary: boolean;
}

export interface TourExpenseRequest {
  expenseNameSnapshot: string;
  description: string;
  actualTransferAmount: number;
  submitForReview: boolean;
}

export interface TourFinancialAdjustmentRequest {
  tourPriceDelta: number;
  expenseBudgetDelta: number;
  customerChargeDelta: number;
  companyAdvanceAmount: number;
  reason: string;
  bookingId?: string;
}

export interface TourFinancialAdjustmentItem {
  id: string;
  adjustmentNo: string;
  targetType: 'TOUR';
  bookingId: string | null;
  tourId: string;
  tourPriceDelta: number | string;
  expenseBudgetDelta: number | string;
  customerChargeDelta: number | string;
  companyAdvanceAmount: number | string;
  reason: string;
  status: string;
  requestedBy: { firstName: string; lastName: string };
  createdAt: string;
  advanceAttachmentPath: string | null;
  advanceAttachmentOriginalName: string | null;
}

export interface TourExpenseItem {
  id: string;
  expenseNo: string;
  bookingId: string | null;
  tourId: string;
  requestedById: string;
  expenseTypeId: string | null;
  expenseNameSnapshot: string;
  source: string;
  wholesaleId: string | null;
  wholesaleNameSnapshot: string | null;
  description: string;
  amount: number | string | null;
  actualTransferAmount: number | string | null;
  attachmentPath: string | null;
  receiptUrl: string | null;
  receiptPath: string | null;
  receiptSubmittedAt: string | null;
  status: string;
  targetType: 'TOUR';
  createdAt: string;
  updatedAt: string;
  paymentTransactions: Array<{ amount: number | string; status: 'PAID' }>;
  requestedBy: {
    firstName: string;
    lastName: string;
    department: { name: string };
  };
  expenseType: { name: string } | null;
  wholesale: { name: string } | null;
}

export interface TourItem {
  id: string;
  tourNo: string;
  tourCode: string;
  tourName: string;
  country: string;
  tourType: TourType;
  travelDate: string;
  returnDate: string | null;
  tourprice: number | string;
  tourTotalAmount: number | string;
  estimatedExpense: number | string;
  com_sale: number | string;
  com_admintrator: number | string;
  programDetail: string | null;
  profilePath: string | null;
  tourPdfDetailPath: string | null;
  requiredPax: number | null;
  status: TourStatus;
  note: string | null;
  createdById: string;
  createdBy: { firstName: string; lastName: string } | null;
  administrators: TourAdministratorItem[];
  commissionRecoveryBalances?: Array<{
    recipientUserId: string;
    outstandingAmount: number | string;
  }>;
  tourCommissions: Array<{
    id: string;
    commissionNo: string;
    administratorId: string;
    amount: number | string;
    commissionRate: number | string | null;
    pax: number | null;
    status: string;
    administrator: { firstName: string; lastName: string };
    paymentTransaction: {
      amount: number | string;
      status: 'PAID' | 'CANCELLED';
    } | null;
    recoveryApplications: Array<{
      amount: number | string;
      recovery: {
        id: string;
        originalAmount: number | string;
        recoveryAmount: number | string;
        settledAmount: number | string;
        remainingAmount: number | string;
        status: 'PENDING' | 'PARTIAL' | 'SETTLED' | 'WAIVED' | 'CANCELLED';
        reason: string;
        createdAt: string;
        booking: { bookingNo: string };
        bookingRefund: {
          refundNo: string;
          refundType: 'PARTIAL' | 'FULL';
          amount: number | string;
          status: string;
        };
        applications: Array<{
          id: string;
          targetType: 'BOOKING_COMMISSION' | 'TOUR_COMMISSION';
          amount: number | string;
          status: 'RESERVED' | 'APPLIED' | 'RELEASED';
          appliedAt: string | null;
          createdAt: string;
          bookingCommission: { commissionNo: string } | null;
          tourCommission: { commissionNo: string } | null;
        }>;
      };
    }>;
  }>;
  bookings: Array<{
    id: string;
    _count: { booking_payments: number };
    bookingNo: string;
    bookingType: TourType;
    pax: number;
    discount: number | string | null;
    totalAmount: number | string;
    paymentStatus: string;
    status: string;
    booking_customer: { name: string };
    booking_payments: Array<{
      customerPaidAmount: number | string;
      withholdingTaxAmount: number | string;
      creditCardFeeAmount: number | string | null;
      creditCardFeeResponsibility: 'CUSTOMER' | 'COMPANY' | null;
      status: string;
    }>;
    booking_refunds: Array<{
      status: string;
      amount: number | string;
      paymentTransaction: {
        amount: number | string;
        status: 'PAID' | 'CANCELLED';
      } | null;
    }>;
  }>;
  expenseRequests: Array<{
    id: string;
    expenseNo: string;
    expenseTypeId: string | null;
    expenseNameSnapshot: string;
    amount: number | string | null;
    status: string;
  }>;
  _count: { bookings: number };
  createdAt: string;
  updatedAt: string;
}

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/tours`;

  async getAll(): Promise<TourItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ tours: TourItem[] }>(this.apiUrl, { withCredentials: true }),
    );
    return response.tours;
  }

  async getCodeOptions(query = ''): Promise<TourCodeOption[]> {
    const response = await firstValueFrom(
      this.http.get<{ tours: TourCodeOption[] }>(`${this.apiUrl}/code-options`, {
        params: query.trim() ? { q: query.trim() } : {},
        withCredentials: true,
      }),
    );
    return response.tours;
  }

  async getById(id: string): Promise<TourItem> {
    const response = await firstValueFrom(
      this.http.get<{ tour: TourItem }>(`${this.apiUrl}/${id}`, { withCredentials: true }),
    );
    return response.tour;
  }

  async closeCase(id: string): Promise<{ commissionCount: number }> {
    const response = await firstValueFrom(
      this.http.post<{ commissionCount: number }>(`${this.apiUrl}/${id}/close-case`, {}, {
        withCredentials: true,
      }),
    );
    return response;
  }

  async submitCommission(tourId: string, commissionId: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(
        `${this.apiUrl}/${tourId}/commissions/${commissionId}/submit`,
        {},
        { withCredentials: true },
      ),
    );
  }

  async getExpenses(id: string): Promise<TourExpenseItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ expenses: TourExpenseItem[] }>(`${this.apiUrl}/${id}/expenses`, {
        withCredentials: true,
      }),
    );
    return response.expenses;
  }

  async createFinancialAdjustment(
    tourId: string,
    input: TourFinancialAdjustmentRequest,
    advanceAttachment: File | null = null,
  ): Promise<TourFinancialAdjustmentItem> {
    const response = await firstValueFrom(
      this.http.post<{ message: string; adjustment: TourFinancialAdjustmentItem }>(
        `${this.apiUrl}/${tourId}/financial-adjustments`,
        this.financialAdjustmentFormData(input, advanceAttachment),
        { withCredentials: true },
      ),
    );
    return response.adjustment;
  }

  private financialAdjustmentFormData(
    input: TourFinancialAdjustmentRequest,
    advanceAttachment: File | null,
  ): FormData {
    const data = new FormData();
    data.append('data', JSON.stringify(input));
    if (advanceAttachment) data.append('advanceAttachment', advanceAttachment);
    return data;
  }

  async getFinancialAdjustments(tourId: string): Promise<TourFinancialAdjustmentItem[]> {
    const response = await firstValueFrom(
      this.http.get<{ adjustments: TourFinancialAdjustmentItem[] }>(
        `${this.apiUrl}/${tourId}/financial-adjustments`,
        { withCredentials: true },
      ),
    );
    return response.adjustments;
  }

  async deleteFinancialAdjustment(tourId: string, adjustmentId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${tourId}/financial-adjustments/${adjustmentId}`, {
        withCredentials: true,
      }),
    );
  }

  async createExpense(
    tourId: string,
    input: TourExpenseRequest,
    attachment: File | null,
  ): Promise<TourExpenseItem> {
    const response = await firstValueFrom(
      this.http.post<{ message: string; expense: TourExpenseItem }>(
        `${this.apiUrl}/${tourId}/expenses`,
        this.expenseFormData(input, attachment),
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  async updateExpense(
    tourId: string,
    expenseId: string,
    input: TourExpenseRequest,
    attachment: File | null,
  ): Promise<TourExpenseItem> {
    const response = await firstValueFrom(
      this.http.put<{ message: string; expense: TourExpenseItem }>(
        `${this.apiUrl}/${tourId}/expenses/${expenseId}`,
        this.expenseFormData(input, attachment),
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  async submitExpenseReceipt(
    tourId: string,
    expenseId: string,
    receipt: File,
  ): Promise<TourExpenseItem> {
    const formData = new FormData();
    formData.append('expenseReceipt', receipt);
    const response = await firstValueFrom(
      this.http.post<{ expense: TourExpenseItem }>(
        `${this.apiUrl}/${tourId}/expenses/${expenseId}/receipt`,
        formData,
        { withCredentials: true },
      ),
    );
    return response.expense;
  }

  private expenseFormData(input: TourExpenseRequest, attachment: File | null): FormData {
    const data = new FormData();
    data.append('expenseNameSnapshot', input.expenseNameSnapshot);
    data.append('description', input.description);
    data.append('actualTransferAmount', String(input.actualTransferAmount));
    data.append('submitForReview', String(input.submitForReview));
    if (attachment) data.append('expenseAttachment', attachment);
    return data;
  }

  async deleteExpense(tourId: string, expenseId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/${tourId}/expenses/${expenseId}`, {
        withCredentials: true,
      }),
    );
  }

  async getExpenseTypes(): Promise<Array<{ id: string; name: string }>> {
    const response = await firstValueFrom(
      this.http.get<{ expenseTypes: Array<{ id: string; name: string }> }>(
        `${this.apiUrl}/expense-types`,
        { withCredentials: true },
      ),
    );
    return response.expenseTypes;
  }

  async getAdministratorOptions(): Promise<TourAdministratorOption[]> {
    const response = await firstValueFrom(
      this.http.get<{ users: TourAdministratorOption[] }>(`${this.apiUrl}/administrator-options`, {
        withCredentials: true,
      }),
    );
    return response.users;
  }

  async createAdministrator(
    tourId: string,
    input: CreateTourAdministratorRequest,
  ): Promise<TourAdministratorItem> {
    const response = await firstValueFrom(
      this.http.post<{ message: string; administrator: TourAdministratorItem }>(
        `${this.apiUrl}/${tourId}/administrators`,
        input,
        { withCredentials: true },
      ),
    );
    return response.administrator;
  }

  async deleteAdministrator(
    tourId: string,
    administratorId: string,
  ): Promise<TourAdministratorItem> {
    const response = await firstValueFrom(
      this.http.delete<{ message: string; administrator: TourAdministratorItem }>(
        `${this.apiUrl}/${tourId}/administrators/${administratorId}`,
        { withCredentials: true },
      ),
    );
    return response.administrator;
  }

  create(
    input: SaveTourRequest,
    profileFile: File | null,
    tourPdfFile: File | null,
  ): Promise<TourItem> {
    return this.saveMultipart(this.apiUrl, input, profileFile, tourPdfFile, 'post');
  }

  update(
    id: string,
    input: SaveTourRequest,
    profileFile: File | null,
    tourPdfFile: File | null,
  ): Promise<TourItem> {
    return this.saveMultipart(
      `${this.apiUrl}/${id}`,
      input,
      profileFile,
      tourPdfFile,
      'put',
    );
  }

  private async saveMultipart(
    url: string,
    input: SaveTourRequest,
    profileFile: File | null,
    tourPdfFile: File | null,
    method: 'post' | 'put',
  ): Promise<TourItem> {
    const formData = new FormData();
    formData.append('data', JSON.stringify(input));
    if (profileFile) formData.append('tourProfile', profileFile);
    if (tourPdfFile) formData.append('tourPdfDetail', tourPdfFile);

    const response = await firstValueFrom(
      this.http.request<{ message: string; tour: TourItem }>(method, url, {
        body: formData,
        withCredentials: true,
      }),
    );
    return response.tour;
  }

  async delete(id: string): Promise<TourItem> {
    const response = await firstValueFrom(
      this.http.delete<{ message: string; tour: TourItem }>(`${this.apiUrl}/${id}`, {
        withCredentials: true,
      }),
    );
    return response.tour;
  }
}
